import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock.ts';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS, HeadlineS } from '@sorare/core/src/atoms/typography';
import { S27Chip } from '@sorare/core/src/atoms/ui/Chip/S27Chip/index.tsx';
import { GameplayTierStars } from '@sorare/core/src/components/card/GameplayTierStars';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { isType } from '@sorare/core/src/lib/gql';
import { css } from '@sorare/css';

import type { RewardSlot_anyRewardConfigInterface } from './__generated__/index.graphql.ts';

export type RewardSlotState = 'claimed' | 'toClaim' | 'future';

const tile = css`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--unit);
  min-height: calc(16 * var(--unit));
  padding: var(--double-unit);
  border-radius: 16px;
  border: 1px solid var(--c-nd-150);
  background: var(--c-nd-50);
`;
const lockedTile = css`
  background: var(--c-nl-500);
`;
const toClaimTile = css`
  border-color: var(--c-blue-400);
  background:
    linear-gradient(
      180deg,
      rgba(var(--c-rgb-blue-400), 0.45) 0%,
      rgba(var(--c-rgb-blue-400), 0.08) 100%
    ),
    var(--c-black);
  box-shadow: 0 0 24px rgba(var(--c-rgb-blue-400), 0.45);
`;
const heroTile = css`
  grid-column: 1 / -1;
  min-height: calc(20 * var(--unit));
`;
const heroRewardTile = css`
  border-color: var(--c-limited);
  background: linear-gradient(
    to top,
    rgba(var(--c-rgb-limited), 0.28) 0%,
    rgba(var(--c-rgb-limited), 0.04) 60%
  );
`;
const heroInner = css`
  width: 100%;
`;
const clickableTile = css`
  appearance: none;
  width: 100%;
  color: inherit;
  cursor: pointer;
`;
const dimmed = css`
  opacity: 0.4;
`;
const badge = css`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
`;

type Props = {
  rewardConfig: RewardSlot_anyRewardConfigInterface;
  state: RewardSlotState;
  dayNumber: number;
  hero?: boolean;
  onClaim?: () => void;
};

const SlotBadge = ({
  state,
  dayNumber,
}: {
  state: RewardSlotState;
  dayNumber: number;
}) => {
  if (state === 'claimed') {
    return (
      <S27Chip color="default" size="small">
        <FontAwesomeIcon icon={iconCheck} />
      </S27Chip>
    );
  }
  if (state === 'toClaim') {
    return (
      <S27Chip color="blue" size="small">
        <FontAwesomeIcon icon={iconGift} />
        <FormattedMessage defaultMessage="To claim" />
      </S27Chip>
    );
  }
  return (
    <S27Chip color="default" size="small">
      <FontAwesomeIcon icon={iconLock} />
      <FormattedMessage
        defaultMessage="Day {dayNumber}"
        values={{ dayNumber }}
      />
    </S27Chip>
  );
};

const rewardPlayer = (rewardConfig: RewardSlot_anyRewardConfigInterface) =>
  isType(rewardConfig, 'CardRewardConfig')
    ? rewardConfig.players?.at(0)
    : undefined;

const rewardLabel = (
  rewardConfig: RewardSlot_anyRewardConfigInterface
): ReactNode => {
  if (
    isType(rewardConfig, 'CardPacksRewardConfig') &&
    rewardConfig.cardPack?.quality
  ) {
    return (
      <FormattedMessage
        defaultMessage="1 {quality, select, GOLD {Gold} SILVER {Silver} BRONZE {Bronze} other {}}"
        values={{ quality: rewardConfig.cardPack.quality }}
      />
    );
  }
  const player = rewardPlayer(rewardConfig);
  if (player?.displayName) {
    return player.displayName;
  }
  return (
    <RewardConfigText
      anyRewardConfig={rewardConfig}
      numberNotation="standard"
    />
  );
};

export const RewardSlot = ({
  rewardConfig,
  state,
  dayNumber,
  hero = false,
  onClaim,
}: Props) => {
  const label = rewardLabel(rewardConfig);
  const player = rewardPlayer(rewardConfig);
  const gameplayTier = player?.gameplayTier;
  const clickable = state === 'toClaim' && !!onClaim;

  const heroName = player ? (
    <Vertical gap={0}>
      <BrandM color="var(--c-white)">{player.firstName}</BrandM>
      <HeadlineS uppercase color="var(--c-white)">
        {player.lastName}
      </HeadlineS>
    </Vertical>
  ) : (
    <BrandM color="var(--c-white)">{label}</BrandM>
  );

  const inner = (
    <>
      <div className={badge}>
        <SlotBadge state={state} dayNumber={dayNumber} />
      </div>
      {hero ? (
        <SBHorizontal
          gap={2}
          className={classNames(heroInner, { [dimmed]: state === 'claimed' })}
        >
          <Vertical gap={0.5}>
            {!!gameplayTier && (
              <div>
                <GameplayTierStars
                  gameplayTier={gameplayTier}
                  variant="flat"
                  size={1.5}
                />
              </div>
            )}
            {heroName}
          </Vertical>
          <RewardConfigImage anyRewardConfig={rewardConfig} size={10} />
        </SBHorizontal>
      ) : (
        <Vertical
          center
          gap={1}
          className={classNames({ [dimmed]: state === 'claimed' })}
        >
          <RewardConfigImage anyRewardConfig={rewardConfig} size={6} />
          <BrandS color="var(--c-white)">{label}</BrandS>
        </Vertical>
      )}
    </>
  );

  const className = classNames(tile, {
    [lockedTile]: state === 'future' && !hero,
    [toClaimTile]: state === 'toClaim',
    [heroTile]: hero,
    [heroRewardTile]: hero && state !== 'toClaim',
    [clickableTile]: clickable,
  });

  return clickable ? (
    <ButtonBase className={className} onClick={onClaim}>
      {inner}
    </ButtonBase>
  ) : (
    <div className={className}>{inner}</div>
  );
};

RewardSlot.fragments = {
  anyRewardConfigInterface: gql`
    fragment RewardSlot_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...RewardConfigImage_anyRewardConfigInterface
      ...RewardConfigText_anyRewardConfigInterface
      ... on CardPacksRewardConfig {
        id
        cardPack {
          slug
          quality
        }
      }
      ... on CardRewardConfig {
        id
        players {
          slug
          displayName
          firstName
          lastName
          gameplayTier
        }
      }
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardSlot_anyRewardConfigInterface>,
};
